/**
 * Cloudflare KV storage implementation
 * This implementation uses HTTP to communicate with the Cloudflare Worker API
 * which in turn manages the KV storage
 */

import { IContentStorage, ContentData, CommonContent, Category, StorageMetadata } from './contentStorage.interface';

export class CloudflareKVStorageService implements IContentStorage {
  private apiUrl = '';
  private apiToken = '';
  private contentCache: ContentData | null = null;
  private cacheTimestamp = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  async init(config?: Record<string, any>): Promise<void> {
    this.apiUrl = config?.apiUrl || process.env.VUE_APP_API_URL || 'http://localhost:8787';
    this.apiToken = config?.apiToken || process.env.VUE_APP_ADMIN_PASSWORD || '';
    console.log('[CloudflareKV] Initialized with API URL:', this.apiUrl);
  }

  async getCommon(): Promise<CommonContent> {
    const content = await this.getAllContent();
    return content.common;
  }

  async getCategory(id: number): Promise<Category | null> {
    const content = await this.getAllContent();
    return content.categories.find(cat => cat.id === id) || null;
  }

  async getAllCategories(): Promise<Category[]> {
    const content = await this.getAllContent();
    return content.categories;
  }

  async getAllContent(): Promise<ContentData> {
    // Check cache first
    if (this.contentCache && Date.now() - this.cacheTimestamp < this.CACHE_TTL) {
      return this.contentCache;
    }

    try {
      const response = await fetch(`${this.apiUrl}/api/content`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch content: ${response.status} ${response.statusText}`);
      }

      const content: ContentData = await response.json();

      // Update cache
      this.contentCache = content;
      this.cacheTimestamp = Date.now();

      console.log('[CloudflareKV] Content loaded from API');
      return content;
    } catch (error) {
      console.error('[CloudflareKV] Failed to load content:', error);
      throw new Error('Failed to load content from API');
    }
  }

  async updateCommon(data: CommonContent): Promise<void> {
    const content = await this.getAllContent();
    content.common = data;
    await this.updateAllContent(content);
  }

  async updateCategory(id: number, data: Category): Promise<void> {
    const content = await this.getAllContent();
    const index = content.categories.findIndex(cat => cat.id === id);

    if (index !== -1) {
      content.categories[index] = data;
      await this.updateAllContent(content);
    } else {
      throw new Error(`Category with id ${id} not found`);
    }
  }

  async updateAllContent(data: ContentData): Promise<void> {
    if (!this.apiToken) {
      throw new Error('Admin password not configured. Cannot update content.');
    }

    try {
      const response = await fetch(`${this.apiUrl}/api/content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.apiToken}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to update content: ${response.status}`);
      }

      // Clear cache after successful update
      this.contentCache = null;
      this.cacheTimestamp = 0;

      console.log('[CloudflareKV] Content updated successfully');
    } catch (error) {
      console.error('[CloudflareKV] Failed to update content:', error);
      throw error;
    }
  }

  async getMetadata(): Promise<StorageMetadata> {
    // For KV storage, we don't track versions separately
    // Return basic metadata
    return {
      version: 1,
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Clear the local cache
   * Useful when you want to force a fresh fetch
   */
  clearCache(): void {
    this.contentCache = null;
    this.cacheTimestamp = 0;
    console.log('[CloudflareKV] Cache cleared');
  }

  /**
   * Helper method to migrate data from local JSON to KV
   * This should only be used during initial setup
   */
  async migrateContent(data: ContentData): Promise<void> {
    if (!this.apiToken) {
      throw new Error('Admin password not configured. Cannot migrate content.');
    }

    try {
      const response = await fetch(`${this.apiUrl}/api/migrate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.apiToken}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to migrate content: ${response.status}`);
      }

      const result = await response.json();
      console.log('[CloudflareKV] Content migrated successfully:', result);

      // Clear cache after migration
      this.contentCache = null;
      this.cacheTimestamp = 0;
    } catch (error) {
      console.error('[CloudflareKV] Failed to migrate content:', error);
      throw error;
    }
  }
}
